import { Form, Input, Select, Switch } from 'antd';
import { FC, useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import ArrowIcon from '../../../../../assets/images/catalogue/arrowIcon';
import { IProductDetailId } from '../../../../../types/products';
import {
  activateOrDeactivateLoadExternalTool,
  getBrandsDetail,
  getProductDetail,
  getProductLine,
  getProfileType
} from '../../../../../services/cataloge';
import { IBrand, IBrands, IProductsLine } from '../../../../../types/category';
import { IProfileDetail, IProfileType } from '../../../../../types/forms';
import GeneralInfoSkeleton from '../../../../../components/formLoaders/generalInfo/GeneralInfoSkeleton';
import { NudosSelectButton } from '../../../../../components/NudosComponents';
import CheckArrowIcon from '../../../../../assets/images/catalogue/CheckArrowIcon';
import useSWRMutation from 'swr/mutation';
import { SWR_KEYS } from '../../../../../constants/constants';
import { displayErrorNotification, displaySuccessNotification } from '../../../../../utils/displayNudosStandardNotifications';
import BordSelectDropdown from 'components/NudosComponents/BordSelectDropdown/BordSelectDropdown';
import './GeneralInfo.scss';

interface ITypeProduct {
  id: number;
  type: string;
}

interface ISelectTab {
  title: string;
  number: number;
  view: boolean;
}

interface IProductForm {
  numberTab: number;
  typeProduct: ITypeProduct;
  productDataDetail?: IProductDetailId;
  setVerifyGeneralInfo: (isVerify: boolean) => void;
  setUpdateInfo: React.Dispatch<React.SetStateAction<boolean>>;
  editIsKit: (isKit: boolean) => void;
  isKit: boolean;
  changeTab?: (dataTab: ISelectTab) => void;
  setProductDataDetail?: (data: IProductDetailId) => void;
  selectedProfiles: number[];
  setSelectedProfiles: React.Dispatch<React.SetStateAction<number[]>>;
}

const General: FC<IProductForm> = ({
  numberTab,
  typeProduct,
  productDataDetail,
  setVerifyGeneralInfo,
  setUpdateInfo,
  editIsKit,
  isKit,
  changeTab,
  setProductDataDetail,
  selectedProfiles,
  setSelectedProfiles
}: IProductForm) => {
  const { Option } = Select;
  const [brandsList, setBrandsList] = useState<IBrands>();
  const [productPositionList, setProductPositionList] = useState<IProductsLine>();
  const [profileTypeList, setProfileTypeList] = useState<IProfileType>();
  const [loading, setLoading] = useState(false);
  const [profilesCodeToNameMapping, setProfilesCodeToNameMapping] = useState<{ [key: string]: string }>({});
  const [showSwitchHover, setShowSwitchHover] = useState(false);
  const [showKitHover, setShowKitHover] = useState(false);

  const form = Form.useFormInstance();
  const { setFieldsValue, setFieldValue } = form;
  const brandId = Form.useWatch('brandId');
  const positionId = Form.useWatch('positionId');
  const description = Form.useWatch('description');
  const profileIds = Form.useWatch('profileIds');

  const {
    trigger: activateOrDeactivateLoadExternalToolTrigger,
    isMutating: isLoadingActivateOrDeactivateLoadExternalTool
  } = useSWRMutation(
    SWR_KEYS.activeOrdeactiveLoadExternalTool,
    async (
      __,
      {
        arg
      }: {
        arg: {
          isActive: boolean;
          id: string | undefined;
        };
      }
    ) => {
      const response = await activateOrDeactivateLoadExternalTool(arg);
      return response;
    },
    {
      onSuccess: async () => {
        const dataUpdated = await getProductDetail(productDataDetail?.productId);

        if (setProductDataDetail) {
          setProductDataDetail(dataUpdated);
          displaySuccessNotification({
            customJSXMessage: (
              <>{productDataDetail?.canBeLoaded ? 'Cargue apagado correctamente' : 'Cargue prendido correctamente'}</>
            )
          });
        }
      },
      revalidate: true
    }
  );

  const getDataSelects = async () => {
    setLoading(true);
    try {
      const listBrands = await getBrandsDetail(typeProduct.id);
      const listBrandsOrdered = listBrands ? listBrands.sort((a, b) => (a.name || '').localeCompare(b.name || '')) : [];
      const listPosition = await getProductLine();
      const profileType = await getProfileType();
      setBrandsList(listBrandsOrdered);
      setProductPositionList(listPosition);
      setProfileTypeList(profileType);
      setLoading(false);
      const profileMaping = profileType.reduce<{ [key: string]: string }>((previousValue, currentValue) => {
        const updatedObject = { ...previousValue, [`${currentValue.id}`]: currentValue.name };
        return updatedObject;
      }, {});
      setProfilesCodeToNameMapping(profileMaping);
    } catch (error) {
      setLoading(false);
      displayErrorNotification();
    }
  };

  const verifyGroup = () => {
    if (brandId && profileIds && profileIds.length > 0 && positionId && description) {
      setVerifyGeneralInfo(true);
    } else {
      setVerifyGeneralInfo(false);
    }
  };

  const onChangeSelectedProfile = (profileOption: number) => {
    if (!profileOption) return;
    const existingProfiles = [...selectedProfiles];
    const profileOptionSelectionIndex = existingProfiles.findIndex(savedProfile => savedProfile === profileOption);
    let newSelectedProfiles: number[];

    if (profileOptionSelectionIndex < 0 && existingProfiles.length < 4) {
      newSelectedProfiles = [...selectedProfiles, profileOption];
    } else {
      newSelectedProfiles = existingProfiles.filter(profile => profile !== profileOption);
    }
    setSelectedProfiles(newSelectedProfiles);
    setFieldValue('profileIds', newSelectedProfiles);
  };

  const formPossibleReferencesUI = (rawProfileOptions: IProfileType | undefined): JSX.Element[] => {
    if (rawProfileOptions && rawProfileOptions.length > 0) {
      return rawProfileOptions.map((profileOption: IProfileDetail, i: number) => {
        const isCurrentlySelected = selectedProfiles.some((savedProfile, i) => savedProfile === profileOption.id);
        return (
          <div
            key={`profileOption${i}-${profileOption.id}`}
            className="profileOption"
            onClick={() => onChangeSelectedProfile(profileOption.id)}
          >
            <NudosSelectButton isButtonActive={isCurrentlySelected} colorPalette="black" />
            <div className="optionText">{profileOption.name}</div>
          </div>
        );
      });
    } else {
      return [];
    }
  };

  const getProfilesSelectShowValue = () => {
    if (selectedProfiles.length > 0) {
      const stringArr = selectedProfiles.map(selectedProfile => profilesCodeToNameMapping[`${selectedProfile}`]);
      const profilesText = stringArr.join(', ');
      return profilesText;
    } else return 'Ej. Producto';
  };

  const verifyChangesInProfiles = () => {
    const preloadedProfileIds = productDataDetail?.profiles.map(profile => profile.id);
    if (!selectedProfiles || selectedProfiles?.length < 1) return false;
    if (selectedProfiles?.length !== preloadedProfileIds?.length) return true;
    return preloadedProfileIds?.some((profileId, i) => profileId !== selectedProfiles[i]);
  };

  const syncProfilesWithForm = () => {
    setFieldsValue({ profileIds: selectedProfiles || [] });
    verifyChangesInProfiles() && setUpdateInfo(true);
  };

  useEffect(() => {
    verifyGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, profileIds, positionId, description]);

  useEffect(() => {
    syncProfilesWithForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfiles]);

  useEffect(() => {
    const initiallySelectedProfiles = productDataDetail?.profiles.map(profile => profile.id);
    if (!!initiallySelectedProfiles?.length) setSelectedProfiles(initiallySelectedProfiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDataDetail]);

  useEffect(() => {
    getDataSelects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDataDetail]);

  return (
    <div style={{ display: numberTab !== 1 ? 'none' : '' }}>
      {loading ? (
        <GeneralInfoSkeleton />
      ) : (
        <div className="generalInfo">
          <div>
            <div className="toolLoadingAndSalesContainer">
              <div>
                <span className="title">Cargue herramientas externas</span>
                <div id="customSwitch" className="mt-20">
                  {isLoadingActivateOrDeactivateLoadExternalTool ? (
                    productDataDetail?.canBeLoaded ? (
                      'Apagando...'
                    ) : (
                      'Prendiendo...'
                    )
                  ) : (
                    <div
                      className="relative"
                      onMouseEnter={() => setShowSwitchHover(true)}
                      onMouseLeave={() => setShowSwitchHover(false)}
                    >
                      {productDataDetail?.isKit && showSwitchHover && (
                        <div className="customHover">
                          Esta opción no está disponible porque la herramienta <br />
                          está marcada como ‘Kit’. Para habilitarla, desmarca <br />
                          la casilla de ‘Kit’ y guarda los cambios.
                        </div>
                      )}
                      <Switch
                        checked={productDataDetail?.productId ? productDataDetail?.canBeLoaded : !isKit}
                        checkedChildren="Prendido para cargue"
                        unCheckedChildren="Apagado para cargue"
                        style={{ height: '24px' }}
                        onChange={() => {
                          if (!productDataDetail?.productId) return;

                          const isActive = productDataDetail?.canBeLoaded === true;
                          activateOrDeactivateLoadExternalToolTrigger({
                            isActive,
                            id: String(productDataDetail?.productId)
                          });
                        }}
                        disabled={productDataDetail?.productId ? productDataDetail?.isKit : isKit}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <span className="title">Venta</span>
                <div className="salesSubtitleContainer">
                  Para prender o apagar debes ir a{' '}
                  <span
                    className="salesSubtitle"
                    onClick={() => {
                      if (changeTab) {
                        changeTab({ title: 'Pricing', number: 3, view: true });
                      }
                    }}
                  >
                    Pricing
                  </span>
                </div>
              </div>
            </div>
            <div className="firstSection noMarginTop">
              <div className="largeItem">
                <div className="titleForm">Marca</div>
                <div className="customSelectAnt">
                  <Form.Item>
                    <Form.Item
                      name={'brandId'}
                      noStyle
                      rules={[{ required: true, message: 'campo requerido' }]}
                      initialValue={productDataDetail?.brandId}
                    >
                      <Select
                        className="customStyle"
                        placeholder="Ej. Marca1"
                        suffixIcon={<ArrowIcon className="rotateIcon" />}
                        showSearch
                        filterOption={(input, option) =>
                          String(option?.children || '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {brandsList &&
                          brandsList.map((item: IBrand) => {
                            return (
                              <Option key={uuid()} value={item.id}>
                                {item.name}
                              </Option>
                            );
                          })}
                      </Select>
                    </Form.Item>
                  </Form.Item>
                </div>
              </div>
              <div className="profileContainer">
                <Form.Item name="profileIds" hidden />
                <BordSelectDropdown
                  label="Perfil (Máximo 4)"
                  selectOptions={formPossibleReferencesUI(profileTypeList)}
                  currentValueOrplaceholder={getProfilesSelectShowValue()}
                  isFilled={selectedProfiles.length > 0}
                  closesOnChangeSelection={false}
                />
              </div>
              <div className="mediumItem noMargin">
                <div className="titleForm">Cargos</div>
                <div className="customSelectAnt">
                  <Form.Item>
                    <Form.Item
                      noStyle
                      initialValue={productDataDetail?.positionId}
                      name="positionId"
                      rules={[{ required: true, message: 'campo requerido' }]}
                    >
                      <Select
                        className="customStyle"
                        placeholder="Ej. Alta"
                        suffixIcon={<ArrowIcon className="rotateIcon" />}
                        showSearch
                        filterOption={(input, option) =>
                          String(option?.children || '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {productPositionList &&
                          productPositionList.map(item => {
                            return (
                              <Option key={uuid()} value={item.id}>
                                {item.name}
                              </Option>
                            );
                          })}
                      </Select>
                    </Form.Item>
                  </Form.Item>
                </div>
              </div>
            </div>
            <div className="secondSection">
              <div className="mediumItem">
                <div className="titleForm">Garantia (opcional)</div>
                <Form.Item initialValue={productDataDetail?.warranty} name="warranty">
                  <Input className="inputTabs" placeholder="Ej. 2 años" />
                </Form.Item>
              </div>
              {typeProduct.id === 6 && (
                <div
                  className="containerKitCheck relative"
                  onMouseEnter={() => setShowKitHover(true)}
                  onMouseLeave={() => setShowKitHover(false)}
                  onClick={() => {
                    setUpdateInfo(true);
                    editIsKit(!isKit);
                  }}
                >
                  {productDataDetail?.isKit && showKitHover && (
                    <div className="customHover">Recuerda guardar los cambios</div>
                  )}
                  <div className="containerOrangeCheck">
                    {isKit ? (
                      <div className="checkSelect">
                        <CheckArrowIcon />
                      </div>
                    ) : (
                      <div className="noSelect"></div>
                    )}
                  </div>
                  <div className="titleCheckKit">Marcar como kit</div>
                </div>
              )}
            </div>
          </div>
          <div className="longDescription" style={{ display: 'block' }}>
            <div className="titleForm">Descripción Larga {description ? '(Max 400 caracteres)' : ''}</div>
            <Form.Item initialValue={productDataDetail?.description} name="description">
              <Input.TextArea maxLength={399} className="longDescriptionInput" placeholder="Max 400 caracteres" />
            </Form.Item>
          </div>
          <div className="h-3 w-full bg-white" />
        </div>
      )}
    </div>
  );
};

export default General;
